import * as React from "react";
import { useState, useEffect } from "react";
import styled, { css } from "styled-components";
import { useQueryClient } from "@tanstack/react-query";
import { Trans, useTranslation } from "react-i18next";
import i18n from "i18n";
import { Icon, ProgressBar, RelativeTime, ConfirmDialog, Timestamp } from "components/common";
import { Badge, Button } from "components/bootstrap";
import useParams from "routing/useParams";
import { JobStatus, JobType } from "../Types";
const StatusBadge = styled(Badge)(({ status, theme }) => {
  var _a;
  const { primary, success, info, warning, danger } = theme.colors.variant.dark;
  const statuses = {
    cancelled: warning,
    complete: success,
    error: danger,
    queued: info,
    running: primary
  };
  const color = (_a = statuses[status]) != null ? _a : info;
  return css`
    margin-left: ${theme.spacings.xs};
    background-color: ${color};
    color: ${theme.utils.readableColor(color)};
  `;
});
const StyledIcon = styled(Icon)(
  ({ theme }) => css`
    margin-right: ${theme.spacings.xxs};
    display: flex;
    align-items: center;
  `
);
const StyledProgressBar = styled(ProgressBar)(
  ({ theme }) => css`
    margin-top: ${theme.spacings.xs};
  `
);
const StyledSpan = styled.span(
  ({ theme }) => css`
    margin-right: ${theme.spacings.xxs};
  `
);
const CancelButton = styled(Button)`
  margin-left: auto;
`;
const AcknowledgeButton = styled(Button)(
  ({ theme }) => css`
    margin-left: auto;
    && {
      color: ${theme.colors.variant.light.default};

      &:hover {
        color: ${theme.colors.variant.default};
      }
    }
  `
);
const JobWrapper = styled.div(
  ({ theme }) => css`
    display: flex;
    padding: ${theme.spacings.xxs} 0;
    margin-bottom: ${theme.spacings.xs};

    &:hover {
      background-color: ${theme.colors.variant.lighter.default};
    }
  `
);
const getJobActionDescription = (job) => {
  const archives = job.archive.join(", ");
  const archivesTimestamps = job.timestamp_from && job.timestamp_to ? /* @__PURE__ */ React.createElement(React.Fragment, null, " ", "(", /* @__PURE__ */ React.createElement(Timestamp, { dateTime: job.timestamp_from }), " - ", /* @__PURE__ */ React.createElement(Timestamp, { dateTime: job.timestamp_to }), ")") : "";
  switch (job.job_type) {
    case JobType.Delete:
      return /* @__PURE__ */ React.createElement(
        Trans,
        {
          i18n,
          ns: "enterprise_data_lake",
          i18nKey: "job.delete_description",
          components: {
            code: /* @__PURE__ */ React.createElement("code", null, job.stream_ids.join(", "), archivesTimestamps)
          }
        }
      );
    case JobType.Optimize:
      if (job.archive.length === 0) {
        if (job.status === JobStatus.Runnable) {
          return i18n.t("job.scheduled_optimization_run_at", { ns: "enterprise_data_lake" });
        }
        return i18n.t("job.scheduled_optimization_started", { ns: "enterprise_data_lake" });
      }
      return /* @__PURE__ */ React.createElement(
        Trans,
        {
          i18n,
          ns: "enterprise_data_lake",
          i18nKey: "job.optimization_operation",
          values: { action: job.action, archives },
          components: { code: /* @__PURE__ */ React.createElement("code", null) }
        }
      );
    case JobType.Restore:
      return /* @__PURE__ */ React.createElement(
        Trans,
        {
          i18n,
          ns: "enterprise_data_lake",
          i18nKey: "job.restore_description",
          components: {
            code: /* @__PURE__ */ React.createElement("code", null, archives, archivesTimestamps)
          }
        }
      );
    default:
      return /* @__PURE__ */ React.createElement(
        Trans,
        {
          i18n,
          ns: "enterprise_data_lake",
          i18nKey: "job.unknown_type",
          values: { jobType: job.job_type },
          components: { code: /* @__PURE__ */ React.createElement("code", null) }
        }
      );
  }
};
const Job = ({ job, onCancel, onAcknowledge, index }) => {
  const { t } = useTranslation("enterprise_data_lake");
  const { streamId } = useParams();
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [shouldRefetchOnJobComplete, setShouldRefetchOnJobComplete] = useState(false);
  const queryClient = useQueryClient();
  const jobIsOver = job.status === JobStatus.Complete || job.status === JobStatus.Cancelled || job.status === JobStatus.Error || job.status === JobStatus.Runnable;
  const jobStatus = job.status === JobStatus.Runnable ? "queued" : job.status;
  const handleCancel = async () => {
    await onCancel(job.id);
    setShowConfirmDialog(false);
  };
  useEffect(() => {
    if (job.status === JobStatus.Running || job.status === JobStatus.Runnable) {
      setShouldRefetchOnJobComplete(true);
    }
  }, [job.status]);
  useEffect(() => {
    if (shouldRefetchOnJobComplete && job.status === JobStatus.Complete) {
      queryClient.invalidateQueries({
        queryKey: ["data-lake", streamId]
      });
    }
  }, [shouldRefetchOnJobComplete, job.status, queryClient, streamId]);
  return /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(JobWrapper, { "data-testid": `lake-job-${index}` }, /* @__PURE__ */ React.createElement(StyledIcon, { name: "settings" }), /* @__PURE__ */ React.createElement(StyledSpan, null, getJobActionDescription(job)), /* @__PURE__ */ React.createElement(StyledSpan, null, job.status === JobStatus.Runnable ? /* @__PURE__ */ React.createElement(Timestamp, { dateTime: job.next_time }) : /* @__PURE__ */ React.createElement(RelativeTime, { dateTime: job.started_at })), /* @__PURE__ */ React.createElement(StatusBadge, { status: job.status }, jobStatus), !jobIsOver && job.is_cancelable ? /* @__PURE__ */ React.createElement(
    CancelButton,
    {
      type: "button",
      bsSize: "xs",
      bsStyle: "primary",
      className: "pull-right",
      onClick: () => setShowConfirmDialog(true)
    },
    t("job.cancel_button")
  ) : job.is_ackable && /* @__PURE__ */ React.createElement(
    AcknowledgeButton,
    {
      type: "button",
      bsStyle: "link",
      onClick: () => onAcknowledge(job.id),
      bsSize: "xs",
      className: "pull-right",
      title: t("job.acknowledge")
    },
    /* @__PURE__ */ React.createElement(Icon, { name: "close" })
  )), showConfirmDialog && /* @__PURE__ */ React.createElement(
    ConfirmDialog,
    {
      show: showConfirmDialog,
      onConfirm: handleCancel,
      onCancel: () => setShowConfirmDialog(false),
      title: t("job.cancel_title")
    },
    /* @__PURE__ */ React.createElement("p", null, t("job.cancel_confirm_text", { jobType: job.job_type })),
    /* @__PURE__ */ React.createElement("p", null, t("job.cancel_confirm_question"))
  ), !jobIsOver && /* @__PURE__ */ React.createElement(StyledProgressBar, { bars: [{ value: job.percent_done, bsStyle: "info", animated: true }] }));
};
export default Job;
